import React, { useState, useEffect } from 'react';
import {
  MdViewCarousel,
  MdAdd,
  MdDelete,
  MdEdit,
  MdSave,
  MdCloudUpload,
  MdCheckCircle,
  MdCampaign,
  MdStar,
  MdLink,
  MdShoppingBag,
  MdSearch,
  MdClose
} from 'react-icons/md';

const CMS_STORAGE_KEY = '@vt_wholesale_banners';
const ANNOUNCEMENT_STORAGE_KEY = '@vt_wholesale_announcement';
const PROMO_SECTIONS_STORAGE_KEY = '@vt_wholesale_promo_sections';

const DEFAULT_BANNERS = [
  {
    id: "b1",
    title: "Optical Wholesale Hub",
    subtitle: "Buy & Sell Premium Frames, Prescription Lenses & Equipment Across Verified Stores with Volume Pricing",
    tag: "B2B WHOLESALE",
    imageUrl: "https://images.unsplash.com/photo-1591076482161-42ce6da69f67?w=1200&auto=format&fit=crop&q=80",
    buttonText: "Explore Bulk Deals",
    badge: "Up to 40% Off Bulk",
    enabled: true,
    productId: null,
    product_id: null,
    storeCode: "",
    store_code: "",
    productName: "",
    product_name: ""
  },
  {
    id: "b2",
    title: "Premium Blue-Cut & Progressive Lenses",
    subtitle: "Direct Factory Stock Supply with Complete Courier Tracking & Lens Certifications",
    tag: "SPECIAL SUPPLIER OFFER",
    imageUrl: "https://images.unsplash.com/photo-1574258495973-f010dfbb5371?w=1200&auto=format&fit=crop&q=80",
    buttonText: "Browse Lenses",
    badge: "Fast Dispatch",
    enabled: true,
    productId: null,
    product_id: null,
    storeCode: "",
    store_code: "",
    productName: "",
    product_name: ""
  },
  {
    id: "b3",
    title: "Optical Machinery & Lab Tools",
    subtitle: "Auto-lensmeters, Trial Lens Sets, Edgers & Ultrasonic Cleaners at Store Wholesale Prices",
    tag: "EQUIPMENT & LAB",
    imageUrl: "https://images.unsplash.com/photo-1511499767150-a48a237f0083?w=1200&auto=format&fit=crop&q=80",
    buttonText: "View Equipment",
    badge: "Verified Sellers",
    enabled: true,
    productId: null,
    product_id: null,
    storeCode: "",
    store_code: "",
    productName: "",
    product_name: ""
  }
];

export default function EcommerceCMS() {
  const [activeTab, setActiveTab] = useState('hero_banners'); // 'hero_banners' | 'announcements' | 'promotions'
  const [banners, setBanners] = useState([]);
  const [announcement, setAnnouncement] = useState({
    badge: "B2B WHOLESALE FLASH",
    text: "Bulk Tier Discounts Active: Extra 15% off on 25+ frames & lenses • Certified Pan-India Express Logistics",
    enabled: true
  });
  const [promotions, setPromotions] = useState({
    spotlightTitle: "Exclusive Direct-From-Lab Sourcing",
    spotlightSubtitle: "Verified spectacle manufacturers and lens surfacing laboratories offering bulk slab pricing",
    trustedBadge1: "Pan-India Express Logistics",
    trustedBadge2: "100% GST ITC Claimable",
    trustedBadge3: "Direct Factory Wholesale Slabs"
  });

  const [availableProducts, setAvailableProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [currentEditBanner, setCurrentEditBanner] = useState(null);
  const [savedSuccess, setSavedSuccess] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [imageUploadError, setImageUploadError] = useState('');

  const API_BASE = "https://api.visiontrackcrm.com";

  const resolveImageUrl = (url) => {
    if (!url || typeof url !== "string") return url;
    if (url.startsWith("data:") || url.startsWith("blob:") || url.startsWith("file:")) return url;
    if (url.startsWith("/")) return `${API_BASE}${url}`;
    if (url.includes("/images/")) {
      const path = url.substring(url.indexOf("/images/"));
      return `${API_BASE}${path}`;
    }
    return url;
  };

  const detectImageFormat = (url) => {
    if (!url) return '';
    const clean = String(url).toLowerCase();
    if (clean.includes('data:image/webp') || clean.includes('.webp')) return 'WEBP';
    if (clean.includes('data:image/png') || clean.includes('.png')) return 'PNG';
    if (clean.includes('data:image/jpeg') || clean.includes('.jpeg')) return 'JPEG';
    if (clean.includes('data:image/jpg') || clean.includes('.jpg')) return 'JPG';
    if (clean.includes('data:image/svg') || clean.includes('.svg')) return 'SVG';
    if (clean.includes('data:image/gif') || clean.includes('.gif')) return 'GIF';
    if (clean.includes('data:image/avif') || clean.includes('.avif')) return 'AVIF';
    if (clean.startsWith('data:image/')) return 'IMAGE';
    return 'URL';
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    // Validate that it's an image (any format: webp, jpeg, jpg, png, gif, svg, avif, bmp, tiff)
    const validExts = /\.(webp|jpe?g|png|gif|svg|avif|bmp|tiff)$/i;
    if (!file.type.startsWith('image/') && !validExts.test(file.name)) {
      alert('Please select a valid image file (.webp, .jpeg, .jpg, .png, .gif, .svg, .avif)');
      return;
    }

    setImageUploading(true);
    setImageUploadError('');

    try {
      let uploadedUrl = null;

      // 1. Try uploading to backend Cloudinary upload endpoint
      try {
        const formData = new FormData();
        formData.append('image', file);
        const res = await fetch(`${API_BASE}/ecommerce/upload-image`, {
          method: 'POST',
          body: formData,
        });
        const data = await res.json();
        if (data && data.success && (data.url || data.secure_url)) {
          uploadedUrl = data.url || data.secure_url;
        }
      } catch (backendErr) {
        console.warn('Backend Cloudinary upload error, using FileReader data URL fallback:', backendErr);
      }

      // 2. Fallback to local FileReader base64 Data URL (universal, works for webp, jpeg, png, etc.)
      if (!uploadedUrl) {
        uploadedUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = (err) => reject(err);
          reader.readAsDataURL(file);
        });
      }

      if (uploadedUrl) {
        setCurrentEditBanner((prev) => ({
          ...prev,
          imageUrl: uploadedUrl,
        }));
      }
    } catch (err) {
      console.error('Image upload failed:', err);
      setImageUploadError('Failed to process image file. You can also paste an image URL directly.');
    } finally {
      setImageUploading(false);
      if (e.target) e.target.value = '';
    }
  };

  useEffect(() => {
    loadCMSData();
  }, []);

  const loadCMSData = async () => {
    try {
      // 1. Fetch products for sponsor linking
      try {
        const resProd = await fetch(`${API_BASE}/products/all`);
        const dataProd = await resProd.json();
        if (dataProd.success && Array.isArray(dataProd.data)) {
          setAvailableProducts(dataProd.data);
        }
      } catch (err) {
        console.log("Could not load products for CMS sponsor linking:", err);
      }

      // 2. Fetch banners from PostgreSQL Backend
      const resBanners = await fetch(`${API_BASE}/ecommerce/banners?_t=${Date.now()}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });
      const dataBanners = await resBanners.json();
      if (dataBanners.success && Array.isArray(dataBanners.data) && dataBanners.data.length > 0) {
        const mapped = dataBanners.data.map(b => ({
          id: b.id,
          title: b.title,
          subtitle: b.subtitle,
          tag: b.tag,
          badge: b.badge,
          imageUrl: b.image_url || b.imageUrl,
          buttonText: b.button_text || b.buttonText,
          enabled: b.enabled !== false,
          productId: b.product_id || b.productId,
          product_id: b.product_id || b.productId,
          storeCode: b.store_code || b.storeCode,
          store_code: b.store_code || b.storeCode,
          productName: b.product_name || b.productName,
          product_name: b.product_name || b.productName
        }));
        setBanners(mapped);
        localStorage.setItem(CMS_STORAGE_KEY, JSON.stringify(mapped));
        localStorage.setItem('@visiontrack_wholesale_banners', JSON.stringify(mapped));
      } else {
        const storedBanners = localStorage.getItem(CMS_STORAGE_KEY);
        setBanners(storedBanners ? JSON.parse(storedBanners) : DEFAULT_BANNERS);
      }

      // 3. Announcements
      const resAnn = await fetch(`${API_BASE}/ecommerce/announcement`);
      const dataAnn = await resAnn.json();
      if (dataAnn.success && dataAnn.data) {
        setAnnouncement(dataAnn.data);
        localStorage.setItem(ANNOUNCEMENT_STORAGE_KEY, JSON.stringify(dataAnn.data));
      } else {
        const storedAnn = localStorage.getItem(ANNOUNCEMENT_STORAGE_KEY);
        if (storedAnn) setAnnouncement(JSON.parse(storedAnn));
      }

      const storedPromo = localStorage.getItem(PROMO_SECTIONS_STORAGE_KEY);
      if (storedPromo) setPromotions(JSON.parse(storedPromo));
    } catch (e) {
      const storedBanners = localStorage.getItem(CMS_STORAGE_KEY);
      setBanners(storedBanners ? JSON.parse(storedBanners) : DEFAULT_BANNERS);
    }
  };

  const handleSaveAll = async () => {
    try {
      // 1. Save locally
      localStorage.setItem(CMS_STORAGE_KEY, JSON.stringify(banners));
      localStorage.setItem(ANNOUNCEMENT_STORAGE_KEY, JSON.stringify(announcement));
      localStorage.setItem(PROMO_SECTIONS_STORAGE_KEY, JSON.stringify(promotions));
      localStorage.setItem('@visiontrack_wholesale_banners', JSON.stringify(banners));

      // 2. Persist to PostgreSQL Backend
      await fetch(`${API_BASE}/ecommerce/banners/save-all`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ banners })
      });

      await fetch(`${API_BASE}/ecommerce/announcement/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(announcement)
      });

      setSavedSuccess(true);
      setTimeout(() => setSavedSuccess(false), 3500);
    } catch (e) {
      setSavedSuccess(true);
      setTimeout(() => setSavedSuccess(false), 3500);
    }
  };

  const handleOpenAdd = () => {
    setCurrentEditBanner({
      id: `b_${Date.now()}`,
      title: '',
      subtitle: '',
      tag: 'WHOLESALE EXCLUSIVE',
      imageUrl: 'https://images.unsplash.com/photo-1591076482161-42ce6da69f67?w=1200&auto=format&fit=crop&q=80',
      buttonText: 'View Sponsored Product',
      badge: 'Featured Deal',
      enabled: true,
      productId: null,
      product_id: null,
      storeCode: '',
      store_code: '',
      productName: '',
      product_name: ''
    });
    setProductSearch('');
    setEditModalOpen(true);
  };

  const handleOpenEdit = (banner) => {
    setCurrentEditBanner({
      ...banner,
      productId: banner.productId || banner.product_id || null,
      product_id: banner.productId || banner.product_id || null,
      storeCode: banner.storeCode || banner.store_code || '',
      store_code: banner.storeCode || banner.store_code || '',
      productName: banner.productName || banner.product_name || '',
      product_name: banner.productName || banner.product_name || ''
    });
    setProductSearch('');
    setEditModalOpen(true);
  };

  const handleSelectProduct = (product) => {
    if (!product) {
      setCurrentEditBanner({
        ...currentEditBanner,
        productId: null,
        product_id: null,
        storeCode: '',
        store_code: '',
        productName: '',
        product_name: ''
      });
      return;
    }

    const img = product.image || (Array.isArray(product.images) && product.images[0]) || currentEditBanner.imageUrl;

    setCurrentEditBanner({
      ...currentEditBanner,
      productId: product.id,
      product_id: product.id,
      storeCode: product.store_code || '',
      store_code: product.store_code || '',
      productName: product.product_name || '',
      product_name: product.product_name || '',
      title: currentEditBanner.title ? currentEditBanner.title : product.product_name,
      subtitle: currentEditBanner.subtitle ? currentEditBanner.subtitle : `Wholesale deal from store ${product.store_code} • ₹${product.wholesale_price || product.price}`,
      buttonText: currentEditBanner.buttonText || 'View Sponsored Product',
      badge: currentEditBanner.badge || 'Sponsored Deal',
      imageUrl: currentEditBanner.imageUrl && !currentEditBanner.imageUrl.includes('unsplash') ? currentEditBanner.imageUrl : (img || currentEditBanner.imageUrl)
    });
  };

  const handleSaveBannerModal = async (e) => {
    if (e) e.preventDefault();
    if (!currentEditBanner.title.trim() || !currentEditBanner.imageUrl.trim()) {
      alert('Please provide banner title and image URL');
      return;
    }

    const index = banners.findIndex(b => b.id === currentEditBanner.id);
    let updated;
    if (index >= 0) {
      updated = [...banners];
      updated[index] = currentEditBanner;
    } else {
      updated = [currentEditBanner, ...banners];
    }
    setBanners(updated);
    localStorage.setItem(CMS_STORAGE_KEY, JSON.stringify(updated));
    localStorage.setItem('@visiontrack_wholesale_banners', JSON.stringify(updated));
    setEditModalOpen(false);

    // Auto-persist live to database
    try {
      await fetch(`${API_BASE}/ecommerce/banners/save-all`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ banners: updated })
      });
      setSavedSuccess(true);
      setTimeout(() => setSavedSuccess(false), 3000);
    } catch (err) {
      console.error("Auto-sync banners failed:", err);
    }
  };

  const handleDeleteBanner = async (id) => {
    if (banners.length <= 1) {
      alert('At least one hero banner is required on the wholesale portal.');
      return;
    }
    if (window.confirm('Are you sure you want to remove this banner?')) {
      const updated = banners.filter(b => b.id !== id);
      setBanners(updated);
      localStorage.setItem(CMS_STORAGE_KEY, JSON.stringify(updated));
      localStorage.setItem('@visiontrack_wholesale_banners', JSON.stringify(updated));
      try {
        await fetch(`${API_BASE}/ecommerce/banners/save-all`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ banners: updated })
        });
      } catch (e) {}
    }
  };

  const handleToggleBanner = async (id) => {
    const updated = banners.map(b => b.id === id ? { ...b, enabled: !b.enabled } : b);
    setBanners(updated);
    localStorage.setItem(CMS_STORAGE_KEY, JSON.stringify(updated));
    localStorage.setItem('@visiontrack_wholesale_banners', JSON.stringify(updated));
    try {
      await fetch(`${API_BASE}/ecommerce/banners/save-all`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ banners: updated })
      });
    } catch (e) {}
  };

  // Filter products for modal selector
  const filteredProducts = availableProducts.filter(p => {
    if (!productSearch.trim()) return true;
    const q = productSearch.toLowerCase();
    return (
      (p.product_name && p.product_name.toLowerCase().includes(q)) ||
      (p.brand && p.brand.toLowerCase().includes(q)) ||
      (p.store_code && p.store_code.toLowerCase().includes(q)) ||
      (p.sku && p.sku.toLowerCase().includes(q))
    );
  });

  return (
    <div className="cms-page-container">
      <style>{`
        .cms-page-container {
          padding-bottom: 40px;
          font-family: 'Inter', sans-serif;
        }
        .cms-header-row {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 24px;
          gap: 16px;
          flex-wrap: wrap;
        }
        .cms-title {
          font-size: 1.5rem;
          font-weight: 800;
          color: #0f172a;
          margin: 0 0 4px 0;
        }
        .cms-subtitle {
          font-size: 0.88rem;
          color: #64748b;
          margin: 0;
        }
        .cms-actions-bar {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .btn-primary-publish {
          display: flex;
          align-items: center;
          gap: 8px;
          background: linear-gradient(135deg, #0067B1 0%, #1e40af 100%);
          color: #ffffff;
          border: none;
          padding: 10px 20px;
          border-radius: 10px;
          font-weight: 700;
          font-size: 0.9rem;
          cursor: pointer;
          box-shadow: 0 4px 14px rgba(0, 103, 177, 0.3);
          transition: all 0.2s ease;
        }
        .btn-primary-publish:hover {
          transform: translateY(-1px);
          box-shadow: 0 6px 18px rgba(0, 103, 177, 0.4);
        }
        .btn-secondary-action {
          display: flex;
          align-items: center;
          gap: 6px;
          background: #ffffff;
          color: #334155;
          border: 1px solid #cbd5e1;
          padding: 9px 16px;
          border-radius: 10px;
          font-weight: 600;
          font-size: 0.85rem;
          cursor: pointer;
        }
        .btn-secondary-action:hover {
          background: #f8fafc;
        }
        .success-toast {
          display: flex;
          align-items: center;
          gap: 10px;
          background: #f0fdf4;
          border: 1px solid #86efac;
          color: #166534;
          padding: 12px 18px;
          border-radius: 12px;
          margin-bottom: 20px;
          font-size: 0.9rem;
        }
        .cms-tabs-nav {
          display: flex;
          gap: 10px;
          border-bottom: 2px solid #e2e8f0;
          margin-bottom: 24px;
        }
        .cms-tab-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 18px;
          border: none;
          background: transparent;
          font-size: 0.92rem;
          font-weight: 700;
          color: #64748b;
          cursor: pointer;
          border-bottom: 3px solid transparent;
          margin-bottom: -2px;
          transition: all 0.2s ease;
        }
        .cms-tab-btn.active {
          color: #0067B1;
          border-bottom-color: #0067B1;
          background: #ffffff;
          border-radius: 8px 8px 0 0;
        }
        .card-cms {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          padding: 24px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
          margin-bottom: 24px;
        }
        .section-header-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }
        .section-heading-text {
          font-size: 1.15rem;
          font-weight: 800;
          color: #0f172a;
          margin: 0;
        }
        .banners-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
          gap: 20px;
        }
        .banner-manage-card {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 14px;
          overflow: hidden;
          transition: all 0.2s ease;
          display: flex;
          flex-direction: column;
        }
        .banner-manage-card:hover {
          border-color: #94a3b8;
          box-shadow: 0 6px 16px rgba(0,0,0,0.06);
        }
        .banner-img-preview-box {
          height: 160px;
          position: relative;
          background: #0f172a;
        }
        .banner-img-preview-box img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .banner-badge-overlay {
          position: absolute;
          top: 10px;
          left: 10px;
          background: #f59e0b;
          color: #000;
          font-size: 0.7rem;
          font-weight: 900;
          padding: 3px 8px;
          border-radius: 6px;
          text-transform: uppercase;
        }
        .banner-status-pill {
          position: absolute;
          top: 10px;
          right: 10px;
          background: rgba(15, 23, 42, 0.85);
          color: #fff;
          font-size: 0.7rem;
          font-weight: 700;
          padding: 3px 8px;
          border-radius: 6px;
        }
        .banner-card-body {
          padding: 16px;
          flex: 1;
        }
        .banner-tag-label {
          font-size: 0.72rem;
          font-weight: 800;
          color: #0067B1;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .banner-title-text {
          font-size: 1.05rem;
          font-weight: 800;
          color: #0f172a;
          margin: 4px 0 6px 0;
          line-height: 1.3;
        }
        .banner-sub-text {
          font-size: 0.8rem;
          color: #64748b;
          line-height: 1.4;
          margin-bottom: 12px;
        }

        /* SPONSOR ATTACHMENT BADGE ON CARD */
        .banner-sponsor-badge-box {
          background: #fffbeb;
          border: 1px solid #fef3c7;
          border-radius: 8px;
          padding: 8px 12px;
          margin-bottom: 12px;
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 0.8rem;
          color: #92400e;
        }

        .sponsor-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #f59e0b;
        }

        .banner-card-controls {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-top: 12px;
          border-top: 1px solid #e2e8f0;
        }
        .control-btns-group {
          display: flex;
          gap: 6px;
        }
        .icon-action-btn {
          background: #ffffff;
          border: 1px solid #cbd5e1;
          padding: 6px;
          border-radius: 6px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #475569;
        }
        .icon-action-btn.delete:hover {
          background: #fee2e2;
          color: #dc2626;
          border-color: #fca5a5;
        }
        .form-grid-2 {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }
        .form-group-cms {
          margin-bottom: 16px;
        }
        .form-label-cms {
          display: block;
          font-size: 0.8rem;
          font-weight: 700;
          color: #475569;
          margin-bottom: 6px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .form-input-cms, .form-textarea-cms, .form-select-cms {
          width: 100%;
          padding: 9px 12px;
          border: 1px solid #cbd5e1;
          border-radius: 8px;
          font-size: 0.9rem;
          color: #0f172a;
          box-sizing: border-box;
          outline: none;
          background: #ffffff;
        }
        .form-input-cms:focus, .form-textarea-cms:focus, .form-select-cms:focus {
          border-color: #0067B1;
          box-shadow: 0 0 0 3px rgba(0, 103, 177, 0.15);
        }
        .modal-overlay-cms {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(15, 23, 42, 0.6);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }
        .modal-card-cms {
          background: #ffffff;
          border-radius: 16px;
          width: 100%;
          max-width: 680px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 20px 25px -5px rgba(0,0,0,0.2);
          padding: 24px;
        }
        .modal-header-cms {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          border-bottom: 1px solid #e2e8f0;
          padding-bottom: 12px;
        }

        /* SPONSOR ATTACHMENT BOX IN MODAL */
        .sponsor-attach-box {
          background: #f0fdf4;
          border: 1.5px solid #86efac;
          border-radius: 12px;
          padding: 16px;
          margin-bottom: 18px;
        }

        .sponsor-attach-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 10px;
        }

        .sponsor-attach-title {
          font-size: 0.92rem;
          font-weight: 800;
          color: #166534;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .sponsor-attach-desc {
          font-size: 0.8rem;
          color: #15803d;
          margin-bottom: 12px;
          line-height: 1.4;
        }

        .attached-product-card {
          background: #ffffff;
          border: 1px solid #bbf7d0;
          border-radius: 10px;
          padding: 12px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          margin-top: 8px;
        }

        .attached-product-info {
          display: flex;
          align-items: center;
          gap: 12px;
          flex: 1;
        }

        .attached-product-img {
          width: 48px;
          height: 48px;
          border-radius: 8px;
          object-fit: contain;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
        }

        .btn-detach {
          background: #fee2e2;
          color: #dc2626;
          border: 1px solid #fecaca;
          padding: 6px 12px;
          border-radius: 6px;
          font-size: 0.78rem;
          font-weight: 700;
          cursor: pointer;
        }

        .btn-detach:hover {
          background: #fca5a5;
        }

        /* BANNER IMAGE MANAGER & FORMAT SUPPORT */
        .banner-image-manager-box {
          background: #f8fafc;
          border: 1.5px dashed #cbd5e1;
          border-radius: 12px;
          padding: 16px;
          margin-bottom: 16px;
          transition: all 0.2s ease;
        }
        .banner-image-manager-box:hover {
          border-color: #0067B1;
        }
        .format-badges-row {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-wrap: wrap;
          margin-bottom: 12px;
        }
        .format-badge-pill {
          background: #e2e8f0;
          color: #334155;
          font-size: 0.7rem;
          font-weight: 800;
          padding: 3px 8px;
          border-radius: 5px;
          letter-spacing: 0.3px;
        }
        .format-badge-pill.highlight {
          background: #dbeafe;
          color: #1e40af;
          border: 1px solid #bfdbfe;
        }
        .upload-dropzone-btn {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          background: #ffffff;
          border: 1.5px dashed #94a3b8;
          border-radius: 10px;
          padding: 18px 14px;
          cursor: pointer;
          transition: all 0.2s ease;
          width: 100%;
          box-sizing: border-box;
          margin-bottom: 12px;
        }
        .upload-dropzone-btn:hover {
          background: #eff6ff;
          border-color: #0067B1;
        }
        .upload-dropzone-title {
          font-size: 0.88rem;
          font-weight: 800;
          color: #0f172a;
          margin-top: 6px;
        }
        .upload-dropzone-sub {
          font-size: 0.75rem;
          color: #64748b;
          margin-top: 2px;
        }
        .image-preview-card {
          position: relative;
          border-radius: 10px;
          overflow: hidden;
          background: #0f172a;
          height: 160px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 12px;
          border: 1px solid #e2e8f0;
        }
        .image-preview-card img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .detected-format-badge {
          position: absolute;
          top: 8px;
          left: 8px;
          background: rgba(15, 23, 42, 0.85);
          color: #38bdf8;
          border: 1px solid rgba(56, 189, 248, 0.4);
          font-size: 0.72rem;
          font-weight: 900;
          padding: 3px 8px;
          border-radius: 4px;
          text-transform: uppercase;
        }
        .clear-img-btn {
          position: absolute;
          top: 8px;
          right: 8px;
          background: rgba(220, 38, 38, 0.85);
          color: #ffffff;
          border: none;
          font-size: 0.72rem;
          font-weight: 800;
          padding: 4px 10px;
          border-radius: 6px;
          cursor: pointer;
          transition: background 0.2s;
        }
        .clear-img-btn:hover {
          background: #b91c1c;
        }
      `}</style>

      {/* PAGE HEADER */}
      <div className="cms-header-row">
        <div>
          <h1 className="cms-title">E-Commerce Landing Page CMS & Banners</h1>
          <p className="cms-subtitle">
            Super Admin Control Center: Alter hero banners, sponsor product links, announcements, and wholesale landing material for <strong>VisionTrack Wholesale</strong>.
          </p>
        </div>

        <div className="cms-actions-bar">
          <button className="btn-secondary-action" onClick={handleOpenAdd}>
            <MdAdd size={18} /> Add New Banner
          </button>
          <button className="btn-primary-publish" onClick={handleSaveAll}>
            <MdCloudUpload size={18} /> Publish Live to Ecommerce
          </button>
        </div>
      </div>

      {/* SUCCESS TOAST */}
      {savedSuccess && (
        <div className="success-toast">
          <MdCheckCircle size={22} color="#15803d" />
          <div>
            <strong>Published Successfully!</strong>
            <div>Landing page banners, sponsored product links, and announcements have been synchronized live with the wholesale store app.</div>
          </div>
        </div>
      )}

      {/* TABS NAVIGATION */}
      <div className="cms-tabs-nav">
        <button
          className={`cms-tab-btn ${activeTab === 'hero_banners' ? 'active' : ''}`}
          onClick={() => setActiveTab('hero_banners')}
        >
          <MdViewCarousel size={18} /> Hero Carousel Banners ({banners.length})
        </button>

        <button
          className={`cms-tab-btn ${activeTab === 'announcements' ? 'active' : ''}`}
          onClick={() => setActiveTab('announcements')}
        >
          <MdCampaign size={18} /> Flash Announcement Ticker
        </button>

        <button
          className={`cms-tab-btn ${activeTab === 'promotions' ? 'active' : ''}`}
          onClick={() => setActiveTab('promotions')}
        >
          <MdStar size={18} /> Trust Badges & Value Propositions
        </button>
      </div>

      {/* TAB 1: HERO CAROUSEL BANNERS */}
      {activeTab === 'hero_banners' && (
        <div className="card-cms">
          <div className="section-header-row">
            <h2 className="section-heading-text">Active Hero Landing Banners</h2>
            <span style={{ fontSize: '0.85rem', color: '#64748b' }}>
              These high-impact sliders appear on top of the Wholesale Hub with sponsor redirects
            </span>
          </div>

          <div className="banners-grid">
            {banners.map((banner, idx) => (
              <div key={banner.id || idx} className="banner-manage-card">
                <div className="banner-img-preview-box">
                  <img src={resolveImageUrl(banner.imageUrl)} alt={banner.title} />
                  {banner.badge && <span className="banner-badge-overlay">{banner.badge}</span>}
                  <span className="banner-status-pill">{banner.enabled !== false ? '● Active' : '○ Disabled'}</span>
                </div>

                <div className="banner-card-body">
                  <span className="banner-tag-label">{banner.tag || 'PROMO'}</span>
                  <h3 className="banner-title-text">{banner.title}</h3>
                  <p className="banner-sub-text">{banner.subtitle}</p>

                  {/* SPONSORED PRODUCT ATTACHMENT BADGE */}
                  {(banner.productId || banner.product_id) && (
                    <div className="banner-sponsor-badge-box">
                      <span className="sponsor-dot"></span>
                      <span>
                        🎯 Sponsored: <strong>{banner.productName || `Product #${banner.productId || banner.product_id}`}</strong>
                        {banner.storeCode && ` (Store: ${banner.storeCode})`}
                      </span>
                    </div>
                  )}

                  <div className="banner-card-controls">
                    <button
                      type="button"
                      className="btn-secondary-action"
                      style={{ padding: '4px 10px', fontSize: '0.75rem' }}
                      onClick={() => handleToggleBanner(banner.id)}
                    >
                      {banner.enabled !== false ? 'Disable' : 'Enable'}
                    </button>

                    <div className="control-btns-group">
                      <button
                        type="button"
                        className="icon-action-btn"
                        onClick={() => handleOpenEdit(banner)}
                        title="Edit Banner Content"
                      >
                        <MdEdit size={16} />
                      </button>
                      <button
                        type="button"
                        className="icon-action-btn delete"
                        onClick={() => handleDeleteBanner(banner.id)}
                        title="Delete Banner"
                      >
                        <MdDelete size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB 2: FLASH ANNOUNCEMENT TICKER */}
      {activeTab === 'announcements' && (
        <div className="card-cms">
          <h2 className="section-heading-text" style={{ marginBottom: '16px' }}>Top Promotional Flash Announcement Bar</h2>
          <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '20px' }}>
            The sticky announcement ticker appears at the top of every screen on the wholesale website and app.
          </p>

          <div className="form-group-cms">
            <label className="form-label-cms">Ticker Badge Slogan</label>
            <input
              type="text"
              className="form-input-cms"
              value={announcement.badge}
              onChange={(e) => setAnnouncement({ ...announcement, badge: e.target.value })}
              placeholder="e.g. B2B WHOLESALE FLASH / SEASON OFFER"
            />
          </div>

          <div className="form-group-cms">
            <label className="form-label-cms">Announcement Message Text</label>
            <textarea
              rows="3"
              className="form-textarea-cms"
              value={announcement.text}
              onChange={(e) => setAnnouncement({ ...announcement, text: e.target.value })}
              placeholder="e.g. Bulk Tier Discounts Active: Extra 15% off on 25+ frames & lenses..."
            />
          </div>

          <button className="btn-primary-publish" onClick={handleSaveAll} style={{ marginTop: '12px' }}>
            <MdSave size={18} /> Save & Publish Ticker
          </button>
        </div>
      )}

      {/* TAB 3: TRUST BADGES & VALUE PROPOSITIONS */}
      {activeTab === 'promotions' && (
        <div className="card-cms">
          <h2 className="section-heading-text" style={{ marginBottom: '16px' }}>Landing Page Spotlight & Trust Pillars</h2>

          <div className="form-group-cms">
            <label className="form-label-cms">Spotlight Headline</label>
            <input
              type="text"
              className="form-input-cms"
              value={promotions.spotlightTitle}
              onChange={(e) => setPromotions({ ...promotions, spotlightTitle: e.target.value })}
            />
          </div>

          <div className="form-group-cms">
            <label className="form-label-cms">Spotlight Subtitle / Description</label>
            <textarea
              rows="2"
              className="form-textarea-cms"
              value={promotions.spotlightSubtitle}
              onChange={(e) => setPromotions({ ...promotions, spotlightSubtitle: e.target.value })}
            />
          </div>

          <div className="form-grid-2">
            <div className="form-group-cms">
              <label className="form-label-cms">Trust Badge #1</label>
              <input
                type="text"
                className="form-input-cms"
                value={promotions.trustedBadge1}
                onChange={(e) => setPromotions({ ...promotions, trustedBadge1: e.target.value })}
              />
            </div>

            <div className="form-group-cms">
              <label className="form-label-cms">Trust Badge #2</label>
              <input
                type="text"
                className="form-input-cms"
                value={promotions.trustedBadge2}
                onChange={(e) => setPromotions({ ...promotions, trustedBadge2: e.target.value })}
              />
            </div>
          </div>

          <div className="form-group-cms">
            <label className="form-label-cms">Trust Badge #3</label>
            <input
              type="text"
              className="form-input-cms"
              value={promotions.trustedBadge3}
              onChange={(e) => setPromotions({ ...promotions, trustedBadge3: e.target.value })}
            />
          </div>

          <button className="btn-primary-publish" onClick={handleSaveAll} style={{ marginTop: '12px' }}>
            <MdSave size={18} /> Save & Publish Trust Pillars
          </button>
        </div>
      )}

      {/* EDIT / CREATE BANNER MODAL */}
      {editModalOpen && currentEditBanner && (
        <div className="modal-overlay-cms" onClick={() => setEditModalOpen(false)}>
          <div className="modal-card-cms" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-cms">
              <h3 style={{ margin: 0, fontWeight: 800, color: '#0f172a' }}>
                {currentEditBanner.title ? 'Edit Hero Banner' : 'Add New Hero Banner'}
              </h3>
              <button
                type="button"
                onClick={() => setEditModalOpen(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem' }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSaveBannerModal}>
              {/* SPONSORED PRODUCT LINKING SECTION */}
              <div className="sponsor-attach-box">
                <div className="sponsor-attach-header">
                  <div className="sponsor-attach-title">
                    <MdLink size={20} /> Attach Sponsored Product from E-Commerce Store
                  </div>
                  {(currentEditBanner.productId || currentEditBanner.product_id) && (
                    <button
                      type="button"
                      className="btn-detach"
                      onClick={() => handleSelectProduct(null)}
                    >
                      Detach Product
                    </button>
                  )}
                </div>

                <p className="sponsor-attach-desc">
                  Select a wholesale product from the e-commerce store. When users click this banner on the Wholesale Hub, it will automatically redirect to that product from its supplier store.
                </p>

                {/* PRODUCT SEARCH & SELECTOR */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', background: '#fff', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '0 10px', flex: 1 }}>
                    <MdSearch color="#94a3b8" size={18} />
                    <input
                      type="text"
                      placeholder="Quick search products to attach..."
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      style={{ border: 'none', outline: 'none', width: '100%', padding: '8px', fontSize: '0.85rem' }}
                    />
                    {productSearch && (
                      <button
                        type="button"
                        onClick={() => setProductSearch('')}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}
                      >
                        <MdClose />
                      </button>
                    )}
                  </div>
                </div>

                <select
                  className="form-select-cms"
                  value={currentEditBanner.productId || currentEditBanner.product_id || ''}
                  onChange={(e) => {
                    const chosen = availableProducts.find(p => String(p.id) === String(e.target.value));
                    handleSelectProduct(chosen || null);
                  }}
                >
                  <option value="">-- No Attached Product (Generic Promo Banner) --</option>
                  {filteredProducts.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.product_name} • {p.brand || 'Optical'} (Store: {p.store_code}) • ₹{p.wholesale_price || p.price}
                    </option>
                  ))}
                </select>

                {/* ATTACHED PRODUCT PREVIEW */}
                {(currentEditBanner.productId || currentEditBanner.product_id) && (
                  <div className="attached-product-card">
                    <div className="attached-product-info">
                      <MdShoppingBag size={24} color="#16a34a" />
                      <div>
                        <div style={{ fontWeight: 800, fontSize: '0.9rem', color: '#0f172a' }}>
                          {currentEditBanner.productName || `Product #${currentEditBanner.productId}`}
                        </div>
                        <div style={{ fontSize: '0.78rem', color: '#475569' }}>
                          Supplier Store: <strong>{currentEditBanner.storeCode || 'STORE001'}</strong> • Product ID: #{currentEditBanner.productId}
                        </div>
                      </div>
                    </div>
                    <span style={{ fontSize: '0.75rem', fontWeight: 700, background: '#dcfce7', color: '#15803d', padding: '4px 8px', borderRadius: '4px' }}>
                      ✓ Sponsored Link Active
                    </span>
                  </div>
                )}
              </div>

              <div className="form-group-cms">
                <label className="form-label-cms">Banner Title / Headline *</label>
                <input
                  type="text"
                  className="form-input-cms"
                  value={currentEditBanner.title}
                  onChange={(e) => setCurrentEditBanner({ ...currentEditBanner, title: e.target.value })}
                  placeholder="e.g. Premium Titanium Frames & Progressive Lenses"
                  required
                />
              </div>

              <div className="form-group-cms">
                <label className="form-label-cms">Banner Description / Subtitle</label>
                <textarea
                  rows="2"
                  className="form-textarea-cms"
                  value={currentEditBanner.subtitle}
                  onChange={(e) => setCurrentEditBanner({ ...currentEditBanner, subtitle: e.target.value })}
                  placeholder="e.g. Factory-direct wholesale prices with certified courier logistics tracking"
                />
              </div>

              <div className="form-grid-2">
                <div className="form-group-cms">
                  <label className="form-label-cms">Category Tag</label>
                  <input
                    type="text"
                    className="form-input-cms"
                    value={currentEditBanner.tag}
                    onChange={(e) => setCurrentEditBanner({ ...currentEditBanner, tag: e.target.value })}
                    placeholder="e.g. BULK EXCLUSIVE / LENS LAB"
                  />
                </div>

                <div className="form-group-cms">
                  <label className="form-label-cms">Promo Badge</label>
                  <input
                    type="text"
                    className="form-input-cms"
                    value={currentEditBanner.badge}
                    onChange={(e) => setCurrentEditBanner({ ...currentEditBanner, badge: e.target.value })}
                    placeholder="e.g. Up to 40% Off / Sponsored"
                  />
                </div>
              </div>

              {/* BANNER IMAGE SELECTION & UPLOAD (ACCEPTS WEBP, JPEG, JPG, PNG, GIF, SVG, AVIF) */}
              <div className="form-group-cms">
                <label className="form-label-cms" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Banner Image (All Formats Supported) *</span>
                  <span style={{ fontSize: '0.72rem', color: '#0284C7', fontWeight: 800 }}>WEBP • JPEG • JPG • PNG • SVG • GIF</span>
                </label>

                <div className="banner-image-manager-box">
                  {/* SUPPORTED FORMAT BADGES */}
                  <div className="format-badges-row">
                    <span style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 700, marginRight: '4px' }}>Formats:</span>
                    <span className="format-badge-pill highlight">.WEBP</span>
                    <span className="format-badge-pill highlight">.JPEG / .JPG</span>
                    <span className="format-badge-pill highlight">.PNG</span>
                    <span className="format-badge-pill">.SVG</span>
                    <span className="format-badge-pill">.GIF</span>
                    <span className="format-badge-pill">.AVIF</span>
                  </div>

                  {/* LIVE PREVIEW IF IMAGE IS SET */}
                  {currentEditBanner.imageUrl ? (
                    <div className="image-preview-card">
                      <img
                        src={resolveImageUrl(currentEditBanner.imageUrl)}
                        alt="Banner Preview"
                        onError={(e) => {
                          e.target.onerror = null;
                        }}
                      />
                      <span className="detected-format-badge">
                        Format: {detectImageFormat(currentEditBanner.imageUrl)}
                      </span>
                      <button
                        type="button"
                        className="clear-img-btn"
                        onClick={() => setCurrentEditBanner({ ...currentEditBanner, imageUrl: '' })}
                      >
                        Remove Image
                      </button>
                    </div>
                  ) : null}

                  {/* FILE UPLOAD DROPZONE */}
                  <label
                    htmlFor="banner-file-input"
                    className="upload-dropzone-btn"
                    style={imageUploading ? { opacity: 0.7, cursor: 'wait' } : {}}
                  >
                    <input
                      type="file"
                      id="banner-file-input"
                      accept="image/webp,image/jpeg,image/jpg,image/png,image/gif,image/svg+xml,image/avif,image/*,.webp,.jpeg,.jpg,.png,.gif,.svg,.avif"
                      onChange={handleFileUpload}
                      style={{ display: 'none' }}
                      disabled={imageUploading}
                    />
                    <MdCloudUpload size={28} color={imageUploading ? '#94a3b8' : '#0067B1'} />
                    <span className="upload-dropzone-title">
                      {imageUploading ? 'Processing & Uploading Image...' : 'Upload Image File (.webp, .jpeg, .jpg, .png, etc.)'}
                    </span>
                    <span className="upload-dropzone-sub">
                      Click to choose from your device • Auto-converts & preserves high resolution
                    </span>
                  </label>

                  {/* ERROR MESSAGE IF ANY */}
                  {imageUploadError ? (
                    <div style={{ color: '#dc2626', fontSize: '0.78rem', marginBottom: '10px', fontWeight: 600 }}>
                      ⚠️ {imageUploadError}
                    </div>
                  ) : null}

                  {/* DIRECT IMAGE URL OR DATA-URI INPUT */}
                  <div style={{ marginTop: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                      <MdLink size={16} color="#64748b" />
                      <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#475569' }}>
                        Or Paste Image Web URL / Data URI:
                      </span>
                    </div>
                    <input
                      type="text"
                      className="form-input-cms"
                      value={currentEditBanner.imageUrl || ''}
                      onChange={(e) => setCurrentEditBanner({ ...currentEditBanner, imageUrl: e.target.value })}
                      placeholder="Paste https://... or data:image/webp;base64,..."
                      required
                      style={{ fontSize: '0.84rem' }}
                    />
                  </div>
                </div>
              </div>

              <div className="form-group-cms">
                <label className="form-label-cms">Button Call-to-Action Label</label>
                <input
                  type="text"
                  className="form-input-cms"
                  value={currentEditBanner.buttonText}
                  onChange={(e) => setCurrentEditBanner({ ...currentEditBanner, buttonText: e.target.value })}
                  placeholder="e.g. View Sponsored Product / Explore Bulk Deals"
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
                <button
                  type="button"
                  className="btn-secondary-action"
                  onClick={() => setEditModalOpen(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary-publish">
                  Save Banner Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
